// src/modules/app-views/leads/pages/Init/modals/ImportLeadsModal/Step2.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Flex, Typography, Form, Select, Alert, theme } from 'antd';
import translateX from 'utils/translateX';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

const { Text } = Typography;
const INTEREST_SEPARATOR = ' | ';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step2Content = ({ columnsMeta, rows, initialMapping }) => {
  const { form } = useSimpleFormContext();
  const { token } = theme.useToken();

  // Pre-fill mapping fields from auto-detect when step mounts
  useEffect(() => {
    if (!initialMapping || !form) return;
    form.setFieldsValue({
      mapping: {
        name: initialMapping.name,
        cpf: initialMapping.cpf,
        email: initialMapping.email,
        phone: initialMapping.phone,
        interest: initialMapping.interest || [],
      },
    });
  }, [initialMapping, form]);

  const options = useMemo(
    () => columnsMeta.map(col => ({
      value: col.index,
      label: `${col.letter} — ${col.header || translateX('no_header')}`,
    })),
    [columnsMeta]
  );

  const interestWatch = Form.useWatch(['mapping', 'interest'], form);
  const previewInterest = useMemo(() => {
    if (!Array.isArray(interestWatch) || interestWatch.length === 0) return '';
    const firstRow = rows?.[0] || [];
    return interestWatch
      .map(idx => String(firstRow?.[idx] ?? '').trim())
      .filter(Boolean)
      .join(INTEREST_SEPARATOR);
  }, [interestWatch, rows]);

  return (
    <Flex vertical gap="middle" className="mt-3">
      <Alert
        type="info"
        showIcon
        title={translateX('map_columns_help_title')}
        description={translateX('map_columns_help_description')}
      />

      <SimpleSelect
        name={['mapping', 'name']}
        label="name"
        options={options}
        required
        showSearch
        optionFilterProp="label"
      />

      <SimpleSelect
        name={['mapping', 'cpf']}
        label="field_cpf"
        options={options}
        required
        showSearch
        optionFilterProp="label"
      />

      <SimpleSelect
        name={['mapping', 'email']}
        label="field_email"
        options={options}
        required={false}
        showSearch
        optionFilterProp="label"
        allowClear
      />

      <SimpleSelect
        name={['mapping', 'phone']}
        label="field_phone"
        options={options}
        required={false}
        showSearch
        optionFilterProp="label"
        allowClear
      />

      <Form.Item
        name={['mapping', 'interest']}
        label={translateX('field_interest')}
        tooltip={translateX('field_interest_tooltip')}
      >
        <Select
          mode="multiple"
          options={options}
          showSearch
          optionFilterProp="label"
          placeholder={translateX('select_interest_columns')}
          allowClear
        />
      </Form.Item>

      {previewInterest && (
        <Alert
          type="success"
          showIcon={false}
          style={{ background: token.colorFillAlter }}
          title={
            <Text>
              <Text type="secondary">{translateX('interest_preview')}: </Text>
              <Text strong>{previewInterest}</Text>
            </Text>
          }
        />
      )}
    </Flex>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const importLeadsStep2 = ({ columnsMeta, rows, initialMapping }) => ({
  stepTitle: 'map_columns',
  content: () => <Step2Content columnsMeta={columnsMeta} rows={rows} initialMapping={initialMapping} />,
  // Validate that email OR phone is mapped (in addition to name + cpf which are required).
  onNext: async ({ form }) => {
    const mapping = form.getFieldValue('mapping') || {};
    if (mapping.name == null) return false;
    if (mapping.cpf == null) return false;
    if (mapping.email == null && mapping.phone == null) return false;
    return true;
  },
  validationFields: [['mapping', 'name'], ['mapping', 'cpf']],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default importLeadsStep2;
